import React, { useRef, useState, useEffect } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { LoadingComponent } from '@blaze-cms/react-page-builder';
import { checkIfLoggedIn } from '@blaze-cms/core-auth-ui';
import parseHTML from 'html-react-parser';
import { useForm } from 'react-hook-form';
import { ajvResolver } from '@hookform/resolvers/ajv';
import FormInput from '../FormInput';
import { UPDATE_USER } from '../../services/mutations';
import { GET_USER } from '../../services/querys';
import { FORM_SCHEMA } from './constants';
import { formatForRender } from '../helpers/format-for-render';
import { normalizeValues } from './helpers/normalize-values';

const UserDetails = ({ modifier, children }) => {
  const { isLoggedIn, id: userId } = checkIfLoggedIn();
  const formRef = useRef(null);
  const { data, loadingUser } = useQuery(GET_USER, {
    variables: { id: userId },
    skip: !userId,
    fetchPolicy: 'network-only',
    staleTime: 0,
    cacheTime: 0
  });
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showTick, setShowTick] = useState(false);
  const [formSchema, setFormSchema] = useState(FORM_SCHEMA);
  const [showSuccessMessage, setSuccessMessage] = useState('');

  const addToast = message => {
    setSuccessMessage(message);
    setTimeout(() => {
      setSuccessMessage('');
    }, 3000);
  };

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    getValues,
    reset
  } = useForm({
    resolver: ajvResolver(formSchema, { strict: false, $data: true, allErrors: true }),
    mode: 'onSubmit',
    reValidateMode: 'onChange'
  });

  useEffect(() => {
    if (!loadingUser && data && data.user) {
      const { user } = data;
      const userData = normalizeValues(user);

      setFormSchema(prevSchema => {
        const updatedSchema = { ...prevSchema };
        updatedSchema.properties.email.value = userData.email;
        return updatedSchema;
      });

      reset(userData, {
        keepDefaultValues: false
      });
    }
  }, [data, loadingUser, reset]);

  const [updateUser, { loading }] = useMutation(UPDATE_USER, {
    onCompleted: async () => {
      const formValues = getValues();
      const { password } = formValues;

      const resetValues = {
        ...formValues,
        password: '',
        passwordConfirm: ''
      };

      reset(resetValues);
      setShowErrorMessage(false);
      setShowSuccess(true);
      setShowTick(true);
      setTimeout(() => setShowTick(false), 4000);

      if (password && password.length > 0) addToast(`Password changed`);
    },
    onError: error => {
      setShowErrorMessage(error.message);
      setShowSuccess(false);
    }
  });

  const handleUpdateUser = async formData => {
    const { firstname, lastname, jobTitle, company, tel, mobile, password, passwordConfirm } =
      formData;
    const user = { firstname, lastname, jobTitle, company, tel, mobile };
    if (password && password.length > 0) {
      user.password = password;
      user.passwordConfirm = passwordConfirm;
    }

    await updateUser({ variables: { input: { user } } });
  };

  const handleSubmitForm = e => handleUpdateUser(e);

  if (loadingUser) return <LoadingComponent />;

  if (!isLoggedIn || !data) return null;

  const parsedModifier = modifier ? ` ${modifier}` : '';

  const formFields = formatForRender(formSchema);

  const getButtonContent = () => {
    if (showTick) return 'Saved';
    if (loading) return <LoadingComponent />;
    return 'Confirm Changes';
  };

  return (
    <div className={`form-container${parsedModifier}`}>
      <form
        onSubmit={handleSubmit(handleSubmitForm)}
        className="form-container__form"
        ref={formRef}>
        {formSchema.sections.map((label, i) => (
          <section key={label}>
            <span className="form-container__sub_header">{label}</span>
            {formFields
              .filter(groups => groups.section === i)
              .map(fieldGroup => (
                <div
                  className="form-container__input_group"
                  key={fieldGroup.section || fieldGroup.fieldId || JSON.stringify(fieldGroup)}>
                  {Array(fieldGroup).map(fields =>
                    fields.fields.map(field => {
                      if (!field.showInForm) return null;
                      if (field.text) {
                        const infoText = field.text.replace(/\&\{value\}/g, field.value ?? '');
                        return (
                          <p className="info" key={field.fieldId}>
                            <strong>{field.label} </strong> {parseHTML(infoText)}
                          </p>
                        );
                      }
                      return (
                        <FormInput
                          key={field.fieldId}
                          id={field.fieldId}
                          label={field.label}
                          isRequired={field.isRequired}
                          formFieldType={field.formFieldType}
                          register={register}
                          placeholder={field.placeholder}
                          error={errors[field.fieldId]}
                          options={field.options}
                          autoComplete={field.autoComplete}
                        />
                      );
                    })
                  )}
                </div>
              ))}
          </section>
        ))}

        {showSuccessMessage && <div className="info">{showSuccessMessage}</div>}

        <button
          className={`button button--center form__submit-button ${
            !isDirty ? 'form__submit-button--condensed' : ''
          }`}
          type="submit"
          disabled={!isDirty || loading || showTick}>
          {getButtonContent()}
        </button>

        {showErrorMessage && (
          <div className="form error_message_wrapper">
            <div className="error_message_icon" />
            <div className="error_message">{showErrorMessage}</div>
          </div>
        )}
      </form>
      {showSuccess && children}
    </div>
  );
};

export default UserDetails;
